import { useTranslation } from 'react-i18next';
import { Skeleton } from 'antd';
import SellerYouHire from './proposals/SellerYouHire';
import EmptyState from '@/components/atoms/EmptyState';
import {
  useGetContractsByPostQuery,
  useGetContractBuyerDetailsQuery,
} from '@/store/services/biddingContractsService';

interface BiddingPostHiredTabProps {
  postId: string;
}

export default function BiddingPostHiredTab({ postId }: BiddingPostHiredTabProps) {
  const { t, i18n } = useTranslation();

  const { data: contractsResponse, isLoading: isContractsLoading } = useGetContractsByPostQuery(
    { postId },
    { skip: !postId }
  );

  const contracts = contractsResponse?.payload ?? [];
  const primaryContract = contracts[0];

  const { data: contractDetailsResponse, isLoading: isDetailsLoading } = useGetContractBuyerDetailsQuery(
    { contractId: primaryContract?.id },
    { skip: !primaryContract?.id }
  );

  const contractDetails = contractDetailsResponse?.payload;
  const isLoading = isContractsLoading || (Boolean(primaryContract?.id) && isDetailsLoading);

  if (isLoading) {
    return (
      <div
        className="bg-light-surface-primary dark:bg-dark-surface-primary rounded-[16px] p-6 border border-light-input-border dark:border-dark-input-border"
        data-testid="bidding-post-hired-tab-loading"
      >
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  if (!contracts.length) {
    return (
      <div
        className="bg-light-surface-primary dark:bg-dark-surface-primary rounded-[16px] p-12 border border-light-input-border dark:border-dark-input-border flex flex-col items-center justify-center text-center"
        data-testid="bidding-post-hired-tab-empty"
      >
        <EmptyState
          disableDesc
          title={t('biddingPosts.noHiredYet')}
          data-testid="bidding-post-hired-empty-state"
        />
      </div>
    );
  }

  const isAr = i18n.language === 'ar';
  const address = contractDetails?.address;
  const location = [
    isAr ? address?.state?.nameAr : address?.state?.nameEn,
    isAr ? address?.country?.nameAr : address?.country?.nameEn,
  ].filter(Boolean).join(', ') || undefined;

  const time = contractDetails?.time ?? primaryContract?.time;
  const timeUnit = contractDetails?.timeUnit?.name ?? primaryContract?.timeUnit?.name;
  const deliveryTime = time && timeUnit ? `${time} ${timeUnit}`.trim() : undefined;

  return (
    <div
      className="bg-light-surface-primary dark:bg-dark-surface-primary rounded-[16px] p-6 border border-light-input-border dark:border-dark-input-border"
      data-testid="bidding-post-hired-tab-container"
    >
      <SellerYouHire
        photo={contractDetails?.sellerPhoto || primaryContract?.sellerImage}
        name={contractDetails?.sellerName || primaryContract?.sellerName}
        companyName={contractDetails?.companyName || contractDetails?.sellerName || primaryContract?.sellerName}
        rating={contractDetails?.sellerReviews}
        reviewsNumber={contractDetails?.sellerReviewsNumber}
        ordersNumber={contractDetails?.sellerOrdersNumber}
        avgResponse={contractDetails?.sellerAvgResponse}
        companyBio={contractDetails?.companyBio || contractDetails?.contractDesc || primaryContract?.desc}
        companySize={contractDetails?.companySize}
        foundedYear={contractDetails?.foundedYear}
        location={location}
        totalPrice={contractDetails?.totalConverted ?? contractDetails?.budgetConverted ?? primaryContract?.budgetConverted ?? primaryContract?.budget}
        deliveryTime={deliveryTime}
        joinedDate={contractDetails?.submittedDate || primaryContract?.submittedDate}
        isVerified={contractDetails?.isVerified ?? primaryContract?.isVerified}
      />
    </div>
  );
}
